import {useEffect, useMemo} from 'react';

import {config} from '../../../config';
import type {AirtableBrand} from '../../../models/airtable';
import type {Department} from '../../../models/middleware';
import {useStores} from '../../../stores/use-stores';
import type {HeaderDisabledEntry} from '../headerDenyList';

import {reportMenuDrift} from './driftReport';
import {megaMenus} from './menuConfig';
import type {MissedLink, ResolvedMenu} from './menuTypes';
import {resolveMenus} from './resolveMenus';

export type MegaMenusResult = {data: ResolvedMenu[]; isLoading: boolean; error: Error | null};

const FAILED = new Error('Departments failed to load');
const NO_MENUS: ResolvedMenu[] = [];
const NO_DEPARTMENTS: Department[] = [];
const NO_BRANDS: AirtableBrand[] = [];

/**
 * Resolves `megaMenus` against the selected store, `useQuery`-shaped. Reads MobX stores, so the
 * caller must be an `observer` (BrandHeader; ruling C3). `enabled` is false when the site header
 * is inactive: no menus, no drift report. `denied` is an input so the deny-list stories drive it.
 */
export const useMegaMenus = ({
  denied,
  enabled,
}: {
  denied: readonly HeaderDisabledEntry[];
  enabled: boolean;
}): MegaMenusResult => {
  const {shopSelectionStore, rangeStore, brandsStore, userStore} = useStores();
  // R59: store fields are read only when enabled, so an inactive (native) observer subscribes
  // to nothing and does not re-render when departments or brands load.
  const selectedStoreId = enabled ? shopSelectionStore.selectedStore?.id : undefined;
  const hasStore = !!selectedStoreId;
  const signedIn = enabled && userStore.isLoggedIn;
  // C2: same store signals as BrandHeader's `variant`; no store keeps menus (spec § 7.4).
  const ecommerce =
    !enabled ||
    !(
      hasStore &&
      (shopSelectionStore.ecommerceDisabled || shopSelectionStore.isSelectedStoreGiftCardStore)
    );
  const departments = enabled ? rangeStore.departments : NO_DEPARTMENTS;
  // Drift events are tagged with the store whose departments were resolved.
  const departmentsStoreId = enabled ? rangeStore.departmentsStoreId : null;
  const departmentsLoadStoreId = enabled ? rangeStore.departmentsLoadStoreId : null;
  const departmentsLoadState = enabled ? rangeStore.departmentsLoadState : 'idle';
  // C1, R64: a load state for another store is stale (reset() has no production caller). The
  // load state belongs to the latest attempt, which a failure also records.
  const loadState =
    departmentsLoadStoreId === (selectedStoreId ?? null) ? departmentsLoadState : 'idle';
  const brands = enabled ? brandsStore.brands : NO_BRANDS;

  const resolved = useMemo(
    () =>
      enabled
        ? resolveMenus({
            menus: megaMenus,
            denied,
            loadState,
            departments,
            brands,
            storeSelected: hasStore,
            signedIn,
            ecommerce,
          })
        : {menus: NO_MENUS, missed: [] as MissedLink[]},
    // `departments` and `brands` are replaced, not mutated, by their stores.
    [enabled, denied, loadState, departments, brands, hasStore, signedIn, ecommerce],
  );

  const {missed} = resolved;
  useEffect(() => {
    if (
      enabled &&
      hasStore &&
      ecommerce &&
      loadState === 'loaded' &&
      departmentsStoreId &&
      departments.length > 0 &&
      missed.length > 0
    ) {
      reportMenuDrift({missed, storeId: departmentsStoreId, tenant: config.BASE_TENANT});
    }
  }, [enabled, hasStore, ecommerce, loadState, departmentsStoreId, departments, missed]);

  const active = enabled && hasStore && ecommerce;
  return {
    data: resolved.menus,
    isLoading: active && loadState === 'idle',
    error: active && loadState === 'failed' ? FAILED : null,
  };
};
